import {
  Component,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { ObjectField, ObjectName } from '@view-model';
import { Observable, ReplaySubject, Subscription } from 'rxjs';
import {
  AutomatorDynamicVariableType,
  AutomatorQueryFilterInput,
  AutomatorRelationalOperator,
  AutomatorWorkflowElementVariable,
} from '@graphql';
import { ObjectTypesSelect } from '../../config';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import {
  ObjectFieldService,
  ObjectNameService,
  RelationalOperatorService,
} from '@element-services';
import { SHARED_IMPORTS } from '@shared/shared-providers';
import { getAvailableDynamicVariables } from '@shared/helpers';
import { ConfigurationFieldControlComponent } from '../configuration-field-control/configuration-field-control.component';
import { UniversalFormControlComponent } from '../universal-form-control/universal-form-control.component';
import { TableInputComponent } from '../table-input/table-input.component';
import { ObjectLabelComponent } from '../object-label/object-label.component';
import { mapToWorkflowElementVariableView } from '../../helpers/variable-view-mapper';

@Component({
  selector: 'app-query-filter-control',
  standalone: true,
  imports: [
    SHARED_IMPORTS,
    ConfigurationFieldControlComponent,
    UniversalFormControlComponent,
    TableInputComponent,
    ObjectLabelComponent,
  ],
  templateUrl: './query-filter-control.component.html',
  styleUrls: ['./query-filter-control.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => QueryFilterControlComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: QueryFilterControlComponent,
      multi: true,
    },
  ],
})
export class QueryFilterControlComponent
  implements OnInit, OnDestroy, OnChanges, ControlValueAccessor, Validator
{
  private readonly formBuilder = inject(FormBuilder);
  private readonly objectFieldService = inject(ObjectFieldService);
  private readonly objectNameService = inject(ObjectNameService);
  private readonly relationalOperatorService = inject(RelationalOperatorService);

  @Input() dynamicVariables: AutomatorWorkflowElementVariable[] = [];
  @Input() objectName!: ObjectName;
  @Input() formSubmitted = false;
  selectedObjectField?: ObjectField;
  filtrationObjectFields: ObjectField[] = [];
  filterOperators$?: Observable<{ label: string; value: AutomatorRelationalOperator }[]>;
  relationalObjectNames$?: Observable<ObjectTypesSelect[]>;

  form!: FormGroup;
  fieldKind?: AutomatorDynamicVariableType;
  typeName?: string;
  availableDynamicVariables: AutomatorWorkflowElementVariable[] = [];

  // Expose the mapper function to the template
  mapToWorkflowElementVariableView = mapToWorkflowElementVariableView;

  private subscription = new Subscription();
  private objectNameSubject = new ReplaySubject<ObjectName>();
  private objectFieldSubject = new ReplaySubject<string>();
  private onChange = (value: any) => value;
  private onTouched = () => {};

  constructor() {
    const formBuilder = this.formBuilder;

    this.form = formBuilder.group({
      objectName: [null, Validators.required],
      objectField: [null, Validators.required],
      operator: [null, Validators.required],
      value: [null, Validators.required],
    });
  }

  ngOnInit() {
    console.log('[QueryFilterControl] ngOnInit called');
    console.log('[QueryFilterControl] objectName input:', this.objectName);

    this.form.valueChanges.subscribe((value) => this.onChange(value));
    this.relationalObjectNames$ = this.relationalObjectNamesByObjectName();

    // Initialize objectNameSubject if objectName is provided
    if (this.objectName) {
      console.log('[QueryFilterControl] Initializing objectNameSubject with:', this.objectName);
      this.objectNameSubject.next(this.objectName);
    }

    // Add debugging for relationalObjectNames$
    this.relationalObjectNames$.subscribe((objectNames) => {
      console.log('[QueryFilterControl] relationalObjectNames$ emitted:', objectNames);
    });

    this.form
      .get('objectName')
      ?.valueChanges.pipe(
        switchMap((objectName) => this.objectFieldService.objectNameToObjectFields(objectName)),
      )
      .subscribe((properties) => {
        console.log('[QueryFilterControl] objectFields received:', properties);
        this.filtrationObjectFields = properties;
        if (this.form.get('objectField')?.value) {
          this.objectFieldSubject.next(this.form.get('objectField')?.value);
        }
      });
    this.filterOperators$ = this.getFilterOperators();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (this.form.invalid) {
      return { invalid: true };
    }
    return null;
  }

  onObjectFieldChange(value: string): void {
    this.availableDynamicVariables = getAvailableDynamicVariables(
      this.dynamicVariables,
      this.form.get('objectName')?.value,
      value === 'id',
    );
    this.objectFieldSubject.next(value);
    this.form.get('operator')?.setValue(null);
    this.form.get('value')?.setValue(null);
  }

  onObjectNameChange(): void {
    this.form.get('objectField')?.setValue(null);
    this.form.get('operator')?.setValue(null);
    this.form.get('value')?.setValue(null);
  }

  onOperatorChange(operator: AutomatorRelationalOperator): void {
    if (operator === 'IS_NULL' || operator === 'IS_NOT_NULL') {
      this.form.get('value')?.disable();
    } else {
      this.form.get('value')?.enable();
    }
  }

  ngOnChanges({ objectName, formSubmitted }: SimpleChanges): void {
    if (objectName) {
      this.objectNameSubject.next(objectName.currentValue);
    }
    if (formSubmitted && formSubmitted.currentValue) {
      this.form.get('objectName')?.markAsTouched();
      this.form.get('objectField')?.markAsTouched();
      this.form.get('operator')?.markAsTouched();
      this.form.get('value')?.markAsTouched();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(value: AutomatorQueryFilterInput): void {
    console.log('[QueryFilterControl] writeValue called with:', value);
    try {
      if (value) {
        this.availableDynamicVariables = getAvailableDynamicVariables(
          this.dynamicVariables,
          value.objectName as ObjectName,
          value.objectField === 'id',
        );

        // Use patchValue instead of setValue to avoid readonly property issues
        this.form.patchValue({
          objectName: value.objectName,
          objectField: value.objectField,
          operator: value.operator,
          value: value.value,
        });

        setTimeout(() => {
          this.form.updateValueAndValidity();
        }, 0);

        if (value.operator) {
          this.onOperatorChange(value.operator);
        }

        console.log('[QueryFilterControl] writeValue completed successfully');
      }
    } catch (error) {
      console.error('[QueryFilterControl] Error in writeValue:', error);
    }
  }

  isValueInputVisible(): boolean {
    return (
      this.form.get('operator')?.value &&
      this.form.get('operator')?.value !== 'IS_NULL' &&
      this.form.get('operator')?.value !== 'IS_NOT_NULL'
    );
  }

  getFilterObjectName(): ObjectName {
    return (this.typeName as ObjectName) || 'Contact';
  }

  getEnumValues(): { label: string; name: string }[] {
    if (!this.selectedObjectField?.enumValues) {
      return [];
    }
    return this.selectedObjectField.enumValues.map((enumValue) => ({
      label: enumValue.label || enumValue.name || '',
      name: enumValue.name || '',
    }));
  }

  private relationalObjectNamesByObjectName(): Observable<ObjectTypesSelect[]> {
    return this.objectNameSubject
      .asObservable()
      .pipe(
        switchMap((objectName) =>
          this.objectNameService.objectNameToFiltrationObjectNames(objectName),
        ),
      );
  }

  private getFilterOperators(): Observable<
    { label: string; value: AutomatorRelationalOperator }[]
  > {
    return this.objectFieldSubject.asObservable().pipe(
      tap(
        (fieldName) =>
          (this.selectedObjectField = this.filtrationObjectFields.find(
            (field) => field.name === fieldName,
          )),
      ),
      filter(() => !!this.selectedObjectField),
      tap(() => (this.fieldKind = this.selectedObjectField?.kind)),
      tap(() => (this.typeName = this.selectedObjectField?.typeName)),
      map((fieldName) => this.filtrationObjectFields.find((field) => field.name === fieldName)),
      filter((field) => !!field),
      map(({ kind }) => this.relationalOperatorService.getOperatorsByKind(kind)),
    );
  }
}
